import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { zNovaPromocao, zProjecaoDePromocao } from '@arena/shared/schemas';
import { adicionarDias } from '@arena/shared/tempo';
import { ErroDeApi, mensagemDoErro } from '../api/cliente.js';
import { siteApi } from '../api/site.js';
import { NOMES_CURTOS_DOS_DIAS, dinheiro } from '../agenda/formatos.js';
import { useAtrasado } from '../agenda/useAtrasado.js';
import { errosPorCampo, primeiraMensagem } from '../alunos/formatos.js';
import { Icone } from '../ui/Icone.js';
import { CorpoDeModal, Modal } from '../ui/Modal.jsx';
import { Botao, Campo, Formulario, Nota } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import { descontoDigitado, diasDaFaixa, faixaCurta } from './formatos.js';

/**
 * `#m-promo` — "Promoção em horário ocioso".
 *
 * Publicada, ela aparece no formulário público e baixa o preço sugerido
 * da locação que começa na faixa. A nota é a conta do protótipo, com a
 * grade de AGORA: horas paradas na faixa na próxima semana, metade delas
 * vendida ao preço com desconto, quatro semanas no mês. O número medido é
 * do BI (17), que abre esta tela a partir da leitura acionável.
 */
export function ModalDePromocao({ hoje, onFechar }: { hoje: string; onFechar: () => void }) {
  const cliente = useQueryClient();
  const avisar = useToast();

  const [dias, setDias] = useState<number[]>([1, 2, 3, 4, 5]);
  const [horaInicio, setHoraInicio] = useState('07:00');
  const [horaFim, setHoraFim] = useState('11:00');
  const [desconto, setDesconto] = useState('30%');
  const [validaAte, setValidaAte] = useState(adicionarDias(hoje, 45));
  const [erros, setErros] = useState<Record<string, string>>({});
  const [falha, setFalha] = useState<string | null>(null);

  const pontos = descontoDigitado(desconto);
  const faixa = useAtrasado(
    { diasSemana: dias, horaInicio, horaFim, descontoPercentual: pontos },
    400,
  );
  const valida = zProjecaoDePromocao.safeParse(faixa);
  const dadosDaProjecao = valida.success ? valida.data : null;
  const projecao = useQuery({
    queryKey: ['site', 'promocoes', 'projecao', dadosDaProjecao],
    queryFn: () => {
      if (!dadosDaProjecao) throw new Error('Faixa inválida para projetar.');
      return siteApi.projetarPromocao(dadosDaProjecao);
    },
    enabled: dadosDaProjecao !== null,
    staleTime: 30_000,
  });

  const criar = useMutation({
    mutationFn: siteApi.criarPromocao,
    onSuccess: () => void cliente.invalidateQueries({ queryKey: ['site'] }),
  });

  function alternar(dia: number) {
    setDias((atual) =>
      atual.includes(dia) ? atual.filter((d) => d !== dia) : [...atual, dia].sort((a, b) => a - b),
    );
  }

  async function publicar() {
    setErros({});
    setFalha(null);
    const validado = zNovaPromocao.safeParse({
      diasSemana: dias,
      horaInicio,
      horaFim,
      descontoPercentual: pontos ?? Number.NaN,
      validaAte,
    });
    if (!validado.success) {
      setErros(errosPorCampo(validado.error.issues));
      return;
    }
    try {
      await criar.mutateAsync(validado.data);
      avisar('Promoção publicada no formulário do site.');
      onFechar();
    } catch (erro) {
      if (erro instanceof ErroDeApi && erro.campos) setErros(primeiraMensagem(erro.campos));
      setFalha(mensagemDoErro(erro));
    }
  }

  return (
    <Modal
      aberto
      onFechar={onFechar}
      largura="estreita"
      titulo="Promoção em horário ocioso"
      descricao={
        dias.length > 0
          ? `${diasDaFaixa(dias)}, das ${faixaCurta(horaInicio, horaFim)}`
          : 'Escolha os dias da faixa'
      }
      rodape={
        <>
          <Botao variante="fantasma" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao onClick={() => void publicar()} disabled={criar.isPending}>
            {criar.isPending ? 'Publicando…' : 'Publicar promoção'}
          </Botao>
        </>
      }
    >
      <CorpoDeModal>
        <Campo rotulo="Dias" erro={erros['diasSemana']}>
          <div className="choices">
            {NOMES_CURTOS_DOS_DIAS.map((rotulo, dia) => (
              <button
                key={rotulo}
                type="button"
                className="choice"
                aria-pressed={dias.includes(dia)}
                onClick={() => alternar(dia)}
              >
                {rotulo}
              </button>
            ))}
          </div>
        </Campo>
        <Formulario colunas={2} onSubmit={(e) => e.preventDefault()}>
          <Campo rotulo="Das" erro={erros['horaInicio']}>
            <input
              type="time"
              step={1800}
              value={horaInicio}
              onChange={(e) => setHoraInicio(e.target.value)}
            />
          </Campo>
          <Campo rotulo="Até" erro={erros['horaFim']}>
            <input
              type="time"
              step={1800}
              value={horaFim}
              onChange={(e) => setHoraFim(e.target.value)}
            />
          </Campo>
          <Campo rotulo="Desconto" erro={erros['descontoPercentual']}>
            <input
              value={desconto}
              onChange={(e) => setDesconto(e.target.value)}
              inputMode="numeric"
            />
          </Campo>
          <Campo rotulo="Validade" erro={erros['validaAte']}>
            <input
              type="date"
              value={validaAte}
              min={hoje}
              onChange={(e) => setValidaAte(e.target.value)}
            />
          </Campo>
        </Formulario>

        {projecao.data && (
          <Nota>
            Hoje são <b>{horasPorExtenso(projecao.data.horasLivresPorSemana)} paradas por semana</b>{' '}
            nessa faixa.{' '}
            {projecao.data.precoHoraComDesconto !== null &&
            projecao.data.receitaMensalEstimada !== null ? (
              <>
                Enchendo metade delas com locação a {dinheiro(projecao.data.precoHoraComDesconto)},
                entram cerca de <b>{dinheiro(projecao.data.receitaMensalEstimada)} por mês</b> sem
                custo adicional de quadra.
              </>
            ) : (
              <>Informe o preço da hora de locação no formulário para ver a projeção de receita.</>
            )}
          </Nota>
        )}

        {falha && (
          <div className="aviso conflito" role="alert">
            <Icone nome="info" />
            <div>{falha}</div>
          </div>
        )}
      </CorpoDeModal>
    </Modal>
  );
}

function horasPorExtenso(horas: number): string {
  const texto = Number.isInteger(horas) ? String(horas) : horas.toFixed(1).replace('.', ',');
  return horas === 1 ? '1 hora' : `${texto} horas`;
}
